/**
 * Guide Page - WNC Localized
 *
 * "The WNC Merchant Playbook" with WNC-flavored examples.
 */

import React from 'react';
import { Footer, GradientHero, Card, SEO, breadcrumbSchema } from '@coupling/ui';
import Header from '../components/Header';
import { useBooking } from '../components/BookingProvider';
import PartnershipGrowthShowcase from '../components/marketing/PartnershipGrowthShowcase';
import { seoConfig } from '../config/seo';
import { existingPosNote, ownPosFeatureList, ownPosHeadline } from '../content/siteMessaging';

export const Guide: React.FC = () => {
    const booking = useBooking();

    return (
        <div className="min-h-screen bg-white">
            <SEO
                title={`The WNC Merchant Playbook - ${seoConfig.siteName}`}
                description="Read the WNC merchant playbook for Coupling POS checkout, online orders, appointments, subscriptions, tips, and local rewards — on the phone you already have."
                canonical={`${seoConfig.siteUrl}/guide`}
                ogImage={seoConfig.defaultOgImage}
                ogUrl={`${seoConfig.siteUrl}/guide`}
                jsonLd={breadcrumbSchema([
                    { name: 'Home', url: seoConfig.siteUrl },
                    { name: 'Guide', url: `${seoConfig.siteUrl}/guide` },
                ])}
            />
            <Header />

            <GradientHero
                title="The WNC Merchant Playbook"
                description="A practical guide to running checkout, appointments, and rewards in Western North Carolina — all in Coupling POS on the phone you already have."
                className="py-16"
            />

            <div className="flex flex-col">

                <section className="py-20 bg-white">
                    <div className="max-w-4xl mx-auto px-4">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="w-12 h-12 rounded-full bg-brand-primary text-white flex items-center justify-center text-2xl font-bold shadow-sm">1</div>
                            <h2 className="text-3xl font-bold text-neutral-900">Run checkout on the phone you already have</h2>
                        </div>
                        <div className="prose prose-lg text-neutral-600 pl-16">
                            <p>
                                Coupling WNC starts with the counter, not a new gadget. Take cards with Tap to Pay on the phone you already carry. Or add an optional Stripe reader available through Coupling.
                            </p>
                            <ul className="list-disc pl-5 space-y-2 mt-4">
                                <li><strong>Tap to Pay:</strong> Accept cards on your phone without buying a new reader.</li>
                                <li><strong>{ownPosHeadline}:</strong> {ownPosFeatureList}.</li>
                                <li><strong>One shop system:</strong> Catalog, checkout, appointments, and rewards live together instead of in four tools.</li>
                                <li>{existingPosNote}</li>
                            </ul>
                        </div>
                    </div>
                </section>

                <section className="py-20 bg-orange-50/50 border-y border-orange-100">
                    <div className="max-w-4xl mx-auto px-4">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="w-12 h-12 rounded-full bg-brand-primary text-white flex items-center justify-center text-2xl font-bold shadow-sm">2</div>
                            <h2 className="text-3xl font-bold text-neutral-900">Mix cash, card, and rewards on one sale</h2>
                        </div>
                        <div className="prose prose-lg text-neutral-600 pl-16">
                            <p>
                                A ticket does not have to be all card or all rewards. Apply what the customer has, collect the rest as cash or card, and keep going.
                            </p>
                            <div className="grid md:grid-cols-3 gap-6 mt-8 not-prose">
                                <Card className="p-6 border-t-4 border-brand-primary shadow-md">
                                    <h3 className="font-bold text-lg mb-2 text-brand-primary-dark">Merchant to Mary</h3>
                                    <p className="text-sm">Mary pays with a card on your phone, plus a $10 reward from last month. You close the remaining balance as usual.</p>
                                </Card>
                                <Card className="p-6 border-t-4 border-brand-secondary shadow-md">
                                    <h3 className="font-bold text-lg mb-2 text-brand-secondary-dark">Mary to Joe</h3>
                                    <p className="text-sm">If you allow it, Mary can pass leftover rewards to Joe. The value is not stuck in one named account.</p>
                                </Card>
                                <Card className="p-6 border-t-4 border-brand-accent shadow-md">
                                    <h3 className="font-bold text-lg mb-2 text-brand-accent">Joe back to you</h3>
                                    <p className="text-sm">Joe spends it at your shop. You stay in charge of where the reward comes home.</p>
                                </Card>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-20 bg-white">
                    <div className="max-w-4xl mx-auto px-4">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="w-12 h-12 rounded-full bg-brand-primary text-white flex items-center justify-center text-2xl font-bold shadow-sm">3</div>
                            <h2 className="text-3xl font-bold text-neutral-900">Catalog, order timing, and appointment booking</h2>
                        </div>
                        <div className="prose prose-lg text-neutral-600 pl-16">
                            <p>
                                Farm stands, massage, and chiropractic shops do not sell the same way. Coupling covers the catalog, when the order is due, and full appointment booking — in the same mobile app that takes the payment.
                            </p>
                            <ul className="list-disc pl-5 space-y-2 mt-4">
                                <li>Build a catalog of what you sell, then check it out with mixed tender.</li>
                                <li>Set order timing when a customer is picking up later.</li>
                                <li>Let customers book the next visit without a separate booking tool.</li>
                            </ul>
                        </div>
                    </div>
                </section>

                <section className="py-20 bg-stone-50 border-t border-stone-100">
                    <div className="max-w-4xl mx-auto px-4">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="w-12 h-12 rounded-full bg-brand-accent text-white flex items-center justify-center text-2xl font-bold shadow-sm">★</div>
                            <h2 className="text-3xl font-bold text-neutral-900">Advanced: WNC merchant connections</h2>
                        </div>

                        <div className="pl-16">
                            <p className="text-lg text-neutral-600 mb-8">
                                Merchant connections are optional expansion. They come after customers and staff already understand checkout and the basic shop offer.
                            </p>
                            <PartnershipGrowthShowcase
                                badge="Advanced merchant partnership example"
                                titleLevel="h3"
                                primaryCta="Book a demo"
                                onPrimaryCta={() => booking.open('demo')}
                            />
                        </div>
                    </div>
                </section>
            </div>

            <Footer />
        </div>
    );
};

export default Guide;
